<template>
  <div class="user-profile-landing">
    <!-- Hero card: greeting + the username from the URL. We don't
         fetch the actual profile from aldo here. the marketing site
         is unauthenticated and we don't want to expose profile
         endpoints publicly without permission gates. The page exists
         purely to (1) catch the link when the OS didn't intercept it
         as a Universal Link / App Link, (2) tell the user the app is
         where the experience lives, (3) offer one-tap install. -->
    <div class="user-profile-landing__card">
      <div class="user-profile-landing__avatar"
aria-hidden="true">
        <span>{{ initials }}</span>
      </div>

      <h1 class="user-profile-landing__title">
        @{{ userName }}
      </h1>
      <p class="user-profile-landing__subtitle">
        is on Bzioup.
      </p>

      <p class="user-profile-landing__body">
        Bzioup is the place where people leave their last words,
        for the time when their voice can no longer say them.
        The full experience lives in the mobile app.
      </p>

      <!-- Store buttons + beta-honest note delegated to the shared
           component so this page and the marketing home stay in
           lockstep (single source of truth for the install URLs +
           beta copy). -->
      <AppDownloadCTA variant="card"
:show-heading="false" />

      <div class="user-profile-landing__divider"></div>

      <router-link to="/"
class="user-profile-landing__home-link">
        ← Back to the Bzioup home
      </router-link>
    </div>
  </div>
</template>

<script>
import AppDownloadCTA from '@/components/Sections/AppDownloadCTA.vue'

export default {
  name: 'UserProfileView',
  components: { AppDownloadCTA },
  computed: {
    /** Username from the URL. purely cosmetic on this page. We
     *  don't validate it against the backend (no public endpoint for
     *  that, and we'd want rate-limiting before adding one). */
    userName() {
      return this.$route.params.userName || ''
    },
    /** First letter of the username, uppercased, for the avatar
     *  placeholder. Underscores in usernames (e.g. `albert_einstein`)
     *  are stripped so we get "A" not "_". */
    initials() {
      const clean = this.userName.replace(/^_+/, '')
      return clean.charAt(0).toUpperCase() || '?'
    },
  },
  /** Try to open the app via Universal Link / App Link the moment
   *  the page lands. If the OS already intercepted the link before
   *  this Vue route loaded (the happy path), we never get here. If
   *  we DO get here, it means: app not installed, OR universal
   *  links not verified by the OS, OR opened from an in-app browser
   *  that strips the deep-link intent (Instagram, WhatsApp, etc.).
   *  In those cases, the page itself is the fallback. no further
   *  scripting needed. iOS Safari especially won't auto-jump to the
   *  app from a web page (Apple's restriction); the smart-app-banner
   *  meta tag is the only path, which you'll add once you have a
   *  store ID. */
  mounted() {
    document.title = `@${this.userName} · Bzioup`
  },
}
</script>

<style lang="scss" scoped>
.user-profile-landing {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--bzioup-bg-color-Z, #f7f6f3);
}

.user-profile-landing__card {
  max-width: 480px;
  width: 100%;
  background: var(--bzioup-bg-color-A, #fff);
  border-radius: 16px;
  padding: 32px 28px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(8, 20, 72, 0.08);
}

.user-profile-landing__avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: var(--bzioup-bg-color-Z, #f7f6f3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
  font-weight: 600;
  color: var(--bzioup-color-pink);
}

.user-profile-landing__title {
  margin: 0;
  font-size: 28px;
  line-height: 1.3;
}

.user-profile-landing__subtitle {
  margin: 4px 0 20px;
  font-size: 15px;
  color: var(--bzioup-text-color-soft, #777);
}

.user-profile-landing__body {
  margin: 0 0 28px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--bzioup-text-color, #333);
}

/* CTA buttons + beta-honest note styles live in
   components/Sections/AppDownloadCTA.vue. Kept divider below for
   the visual separation between CTA and back-to-home link. */
.user-profile-landing__divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 24px 0 20px;
}

.user-profile-landing__home-link {
  display: inline-block;
  font-size: 13px;
  color: var(--bzioup-text-color-soft, #888);
  text-decoration: none;

  &:hover { color: var(--bzioup-color-pink); }
}
</style>
